import type { ResultOf } from "@opensea/graphql"
import type { CollectionOverviewMediaType } from "@/components/collection/overview/types"
import { getMediaPropsFromMedia } from "@/components/collection/overview/utils"
import type { CarouselModuleTypes } from "@/components/studio/page-editor/types"
import type { ContentBlockCardProps } from "../components/ContentBlockCard/ContentBlockCard"
import type { ContentBlockCarouselFragment } from "../components/ContentBlockCarousel/ContentBlockCarousel.fragment"

type CarouselType = Exclude<CarouselModuleTypes, "TEAM">

type CarouselData = {
  carouselType: CarouselType
  sections: ContentBlockCardProps[] | GraphQLSections
}

// Store data types
type ContentBlockSection = {
  __typename: string
  title?: string | null
  description?: string | null
  itemId?: string | null
  contentMedia?: unknown
  timelineMedia?: unknown
  externalLink?: { href: string; label: string } | string | null
  date?: string
} | null

// GraphQL data type
type GraphQLSections = NonNullable<
  ResultOf<typeof ContentBlockCarouselFragment>["sections"]
>

/**
 * Normalize externalLink to proper format for externalUrl
 */
function normalizeExternalLinkForDisplay(
  externalLink: { href: string; label: string } | string | null | undefined,
): { href: string; label: string } | null {
  if (!externalLink) {
    return null
  }

  // If it's already an object with href, return normalized version
  if (typeof externalLink === "object" && externalLink.href) {
    return {
      href: externalLink.href,
      label: externalLink.label || "Learn more",
    }
  }

  // If it's a string, create proper object
  if (typeof externalLink === "string" && externalLink.trim()) {
    return {
      href: externalLink.trim(),
      label: "Learn more",
    }
  }

  return null
}

/**
 * Determines carousel type from sections data
 */
// biome-ignore lint/suspicious/noExplicitAny: TODO FIX
function getCarouselType(firstSection: any): CarouselType {
  if (!firstSection?.__typename) {
    return "FREEFORM"
  }

  switch (firstSection.__typename) {
    case "DefaultContentBlock":
      return "FREEFORM"
    case "TimelineContentBlock":
      return "TIMELINE"
    case "FeatureContentBlock":
      if (firstSection.itemId) {
        return "FEATURED_ITEMS"
      }
      return "FEATURED_COLLECTIONS"
    default:
      return "FREEFORM"
  }
}

/**
 * Transforms store section to ContentBlockCardProps
 */
function transformStoreSection(
  section: ContentBlockSection,
): ContentBlockCardProps | null {
  if (!section) {
    return null
  }

  // Convert contentMedia (unknown) to CollectionOverviewMediaType
  let media: CollectionOverviewMediaType | null = null
  if (section.contentMedia) {
    // Assume contentMedia follows the expected structure
    media = section.contentMedia as CollectionOverviewMediaType
  }

  return {
    title: section.title,
    description: section.description,
    media,
    externalUrl: normalizeExternalLinkForDisplay(section.externalLink),
  }
}

/**
 * Transforms store section to timeline-specific ContentBlockCardProps
 */
function transformStoreTimelineSection(
  section: ContentBlockSection,
): (ContentBlockCardProps & { date?: string }) | null {
  if (!section) {
    return null
  }

  // Convert timelineMedia (for existing data) or contentMedia (for new uploads) to CollectionOverviewMediaType
  let media: CollectionOverviewMediaType | null = null
  if (section.timelineMedia) {
    // Existing GraphQL data uses timelineMedia field
    media = section.timelineMedia as CollectionOverviewMediaType
  } else if (section.contentMedia) {
    // New uploads or form data uses contentMedia
    media = section.contentMedia as CollectionOverviewMediaType
  }

  return {
    title: section.title,
    description: section.description,
    media,
    externalUrl: normalizeExternalLinkForDisplay(section.externalLink),
    date: section.date,
  }
}

/**
 * Transforms GraphQL section to ContentBlockCardProps for freeform carousel
 */
function transformGraphQLFreeformSection(
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX
  section: any,
): ContentBlockCardProps | null {
  if (!section || section.__typename !== "DefaultContentBlock") {
    return null
  }

  return {
    title: section.title,
    description: section.description,
    media: section.contentMedia,
    externalUrl: section.externalLink
      ? {
          href: section.externalLink.href,
          label: section.externalLink.label,
        }
      : null,
  }
}

/**
 * Transforms GraphQL section to ContentBlockCardProps for timeline carousel
 */
function transformGraphQLTimelineSection(
  // biome-ignore lint/suspicious/noExplicitAny: TODO FIX
  section: any,
): (ContentBlockCardProps & { date?: string }) | null {
  if (!section || section.__typename !== "TimelineContentBlock") {
    return null
  }

  const mediaProps = getMediaPropsFromMedia(section.timelineMedia)

  return {
    title: section.title,
    description: section.description,
    media: mediaProps,
    externalUrl: normalizeExternalLinkForDisplay(section.externalLink),
    date: section.date,
  }
}

/**
 * Transforms store data to carousel props
 */
export function transformStoreDataToCarouselProps(
  sections: ContentBlockSection[] | null,
): CarouselData {
  if (!sections?.length) {
    return {
      carouselType: "FREEFORM",
      sections: [],
    }
  }

  const carouselType = getCarouselType(sections[0])

  if (carouselType === "FREEFORM") {
    const transformedSections = sections
      .map(transformStoreSection)
      .filter((section): section is ContentBlockCardProps => section !== null)

    return {
      carouselType,
      sections: transformedSections,
    }
  }

  if (carouselType === "FEATURED_COLLECTIONS") {
    // For featured collections, pass through original sections with collectionSlug
    return {
      carouselType: "FEATURED_COLLECTIONS",
      sections: sections as GraphQLSections,
    }
  }

  // For timeline, properly transform store sections with date field
  if (carouselType === "TIMELINE") {
    const transformedSections = sections
      .map(transformStoreTimelineSection)
      .filter(
        (section): section is ContentBlockCardProps & { date?: string } =>
          section !== null,
      )

    return {
      carouselType,
      sections: transformedSections,
    }
  }

  // Fallback for any other cases
  return {
    carouselType: "FREEFORM",
    sections: [],
  }
}

/**
 * Transforms GraphQL data to carousel props
 */
export function transformGraphQLDataToCarouselProps(
  sections: GraphQLSections | null,
): CarouselData {
  if (!sections?.length) {
    return {
      carouselType: "FREEFORM",
      sections: [],
    }
  }

  const carouselType = getCarouselType(sections[0])

  if (carouselType === "FREEFORM") {
    const transformedSections = sections
      .map(transformGraphQLFreeformSection)
      .filter((section): section is ContentBlockCardProps => section !== null)

    return {
      carouselType,
      sections: transformedSections,
    }
  }

  if (carouselType === "TIMELINE") {
    const transformedSections = sections
      .map(transformGraphQLTimelineSection)
      .filter((section): section is ContentBlockCardProps => section !== null)

    return {
      carouselType,
      sections: transformedSections,
    }
  }

  // For featured items/collections, pass through original GraphQL sections
  return {
    carouselType,
    sections,
  }
}
